import React, { useCallback } from 'react';
import Editor from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import classNameBind from 'classnames/bind';
import { EditorView } from '@codemirror/view';
import { autocompletion } from '@codemirror/autocomplete';
import styles from './CodeEditor.module.css';

const bindClasses = classNameBind.bind(styles);

const CodeEditor = ({
  name,
  value,
  readOnly,
  autoFocus,
  onChange,
  mode,
  fixedHeight,
  fullHeight,
  variableAutocompleteOptions,
}) => {
  const handleChange = useCallback((targetValue) => {
    onChange({
      target: {
        name,
        value: targetValue,
      },
    });
  }, []);

  const completeVariable = useCallback((completionContext) => {
    const variable = completionContext.matchBefore(/(?!({{((?:.(?!}}))*.)}})){{.*/);
    if (variable === null) {
      return null;
    }

    return {
      from: variable.from,
      options: variableAutocompleteOptions.map(option => ({
        label: `{{ ${option.displayName} }}`,
        detail: option.displayName,
        apply: `{{ ${option.variableName} }}`,
        boost: option.importance,
      })),
    };
  }, [variableAutocompleteOptions]);

  const variableAutocompletion = autocompletion({
    icons: false,
    override: [completeVariable],
  });

  const extensions = [
    EditorView.lineWrapping,
  ];

  if (mode !== 'no-highlight') {
    const lang = mode === 'javascript' ? javascript() : html({ autoCloseTags: false });
    extensions.push(lang);
  }

  if (variableAutocompleteOptions !== null) {
    extensions.push(variableAutocompletion);
  }

  const basicSetup = {
    autocompletion: false,
    closeBrackets: false,
    rectangularSelection: false,
    crosshairCursor: false,
    highlightActiveLine: !readOnly,
    highlightActiveLineGutter: !readOnly,
  };

  return (
    <div
      className={bindClasses('editor', { fixedHeight, fullHeight })}
      data-ppuc-id="CodeEditor"
    >
      <Editor
        value={value}
        extensions={extensions}
        autoFocus={autoFocus}
        readOnly={readOnly}
        editable={!readOnly}
        onChange={handleChange}
        basicSetup={basicSetup}
      />
    </div>
  );
};

CodeEditor.defaultProps = {
  readOnly: false,
  autoFocus: false,
  onChange: () => {},
  mode: 'html',
  fixedHeight: false,
  fullHeight: false,
  variableAutocompleteOptions: null,
};

export { CodeEditor };
